import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo } from "react";

import CollapsibleSection from "./CollapsibleSection";
import ExamplePreviewer from "./ExamplePreviewer";
import ExamplesProvider, { ExamplesSelectorConfig, useExamples } from "./examples-store";
import ExampleSelectionDialog from "./ExampleSelectionDialog";
import { DatasetSelect, VersionSelect } from "./selectors";
import { Dataset } from "./types";

import { useDatasetGroups } from "@/queries";
import { useUser } from "@/context/user-context";
import { useAuth } from "@/context/auth-context";
import { Skeleton } from "@/components/ui/skeleton";

export interface ExamplesSelectorProps {
  initialConfig?: ExamplesSelectorConfig;
  onChange?: (config: ExamplesSelectorConfig) => void;
  readOnly?: boolean;
  showCollapsible?: boolean;
  size?: "small" | "medium" | "large";
  className?: string;
  onDelete?: () => void;
  title?: string;
}

export { ExamplesProvider, useExamples } from "./examples-store";
export type { ExamplesSelectorConfig } from "./examples-store";
export type { Dataset, Example } from "./types";

const ExamplesSelectorSkeleton = ({
  readOnly,
  showCollapsible,
  size,
}: {
  readOnly?: boolean;
  showCollapsible?: boolean;
  size?: "small" | "medium" | "large";
}) => {
  const content = (
    <div className="relative flex w-full flex-col space-y-4">
      <Skeleton width="100%" height="2rem" />
      <Skeleton width="100%" height="2rem" />
      <Skeleton width="100%" height="8rem" />
    </div>
  );

  if (showCollapsible) {
    return (
      <CollapsibleSection
        title="Select Examples from Dataset"
        size={readOnly ? "small" : size || "medium"}
      >
        {content}
      </CollapsibleSection>
    );
  }

  return content;
};

const ExamplesSelectorContent = observer(
  ({
    onChange,
    readOnly = false,
    showCollapsible = false,
    size = "medium",
    className = "",
    onDelete,
    title,
  }: Omit<ExamplesSelectorProps, "initialConfig">) => {
    const store = useExamples();

    const { data, isLoading } = useDatasetGroups(
      useAuth()?.userToken ?? "",
      Number(useUser()?.activeWorkspaceId),
    );
    const dataset_groups = useMemo(() => data?.dataset_groups || [], [data]);
    const memoizedOnChange = useCallback(
      (config: ExamplesSelectorConfig) => {
        if (onChange) {
          onChange(config);
        }
      },
      [onChange],
    );

    useEffect(() => {
      // Defer the onChange callback to avoid synchronous updates during render
      const timeoutId = setTimeout(() => {
        memoizedOnChange(store.config);
      }, 0);

      return () => clearTimeout(timeoutId);
    }, [store.config, memoizedOnChange]);

    const selectedDatasetId = useMemo(
      () =>
        dataset_groups
          .find(({ name }: { name: string }) => name === store.selectedDataset)
          ?.datasets.find((dataset: Dataset) => dataset.version_number === store.selectedVersion)
          ?.id,
      [dataset_groups, store.selectedDataset, store.selectedVersion],
    );

    const columnDummyData = useMemo(
      () =>
        store.visibleColumns.reduce(
          (acc, column) => {
            acc[column] = "Example Value";
            return acc;
          },
          {} as Record<string, string>,
        ),
      [store.visibleColumns],
    );

    const displayTitle = useMemo(
      () =>
        title ||
        (store.selectedDataset
          ? `${store.selectedDataset}${
              store.selectedVersion ? ` v${store.selectedVersion}` : ""
            } (${store.selectedExamples.length})`
              .slice(0, 50)
              .concat(store.selectedDataset.length > 50 ? "..." : "")
          : "Select Dataset Examples"),
      [title, store.selectedDataset, store.selectedVersion, store.selectedExamples.length],
    );

    const handleDatasetSelect = useCallback(
      (dataset: string) => {
        store.setSelectedDataset(dataset);
      },
      [store],
    );

    const handleVersionChange = useCallback(
      (version: number) => {
        store.setSelectedVersion(version);
      },
      [store],
    );

    const handleAddExample = useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (example: any) => {
        store.addExample(example);
      },
      [store],
    );

    const handleDataTypeChange = useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (type: any) => {
        if (!readOnly) {
          store.setDataType(type);
        }
      },
      [readOnly, store],
    );

    if (isLoading) {
      return (
        <ExamplesSelectorSkeleton
          readOnly={readOnly}
          showCollapsible={showCollapsible}
          size={size}
        />
      );
    }

    const content = (
      <div className="flex flex-col gap-y-3">
        <DatasetSelect
          isReadOnly={readOnly}
          selectedDatasetName={store.selectedDataset}
          setSelectedDatasetName={handleDatasetSelect}
          datasetId={selectedDatasetId}
        />
        <VersionSelect
          isReadOnly={readOnly || !selectedDatasetId}
          selectedDataset={store.selectedDataset}
          selectedVersion={store.selectedVersion}
          onVersionChange={handleVersionChange}
        />
        {!readOnly && selectedDatasetId && store.selectedDataset && store.selectedVersion > 0 && (
          <ExampleSelectionDialog
            key="example-selection"
            datasetId={selectedDatasetId}
            selectedExamples={store.selectedExamples}
            onAddExample={handleAddExample}
          />
        )}
        <div className="my-4 h-px w-full bg-gray-200" />
        <ExamplePreviewer
          data={columnDummyData}
          displayType={store.dataType}
          onChange={handleDataTypeChange}
        />
      </div>
    );

    if (showCollapsible) {
      return (
        <CollapsibleSection
          title={displayTitle}
          size={readOnly ? "small" : size}
          onDelete={onDelete}
          className={className}
        >
          {content}
        </CollapsibleSection>
      );
    }

    return <div className={className}>{content}</div>;
  },
);

const ExamplesSelector = ({ initialConfig, ...rest }: ExamplesSelectorProps) => (
  <ExamplesProvider initialConfig={initialConfig}>
    <ExamplesSelectorContent {...rest} />
  </ExamplesProvider>
);

export default ExamplesSelector;
